export interface UavSurveyTagSelection {
  tagIds: string[];
  tagNameHints: Record<string, string>;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function asId(value: unknown): string {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  const rec = asRecord(value);
  const id = rec?.['id'] ?? rec?.['tagId'];
  return typeof id === 'string' && id.trim() ? id.trim() : '';
}

function asName(value: unknown): string {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  const rec = asRecord(value);
  const name = rec?.['name'] ?? rec?.['tagName'];
  return typeof name === 'string' && name.trim() ? name.trim() : '';
}

function asIdList(value: unknown): string[] {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map(asId).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(/[,;]/)
      .map((part) => part.trim())
      .filter(Boolean);
  }
  const id = asId(value);
  return id ? [id] : [];
}

function asNameList(value: unknown): string[] {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map(asName).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(/[,;]/)
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return [];
}

/** Reads tag ids/names from the UAV survey JSON regardless of tags vs tagIds vs tagNames shape. */
export function resolveUavSurveyTags(survey: unknown): UavSurveyTagSelection {
  const rec = asRecord(survey) ?? {};
  const hints: Record<string, string> = {};
  const ids: string[] = [];

  const tagged = rec['tags'] ?? rec['uavTags'];
  if (Array.isArray(tagged)) {
    for (const item of tagged) {
      const id = asId(item);
      const name = asName(item);
      if (id && !ids.includes(id)) {
        ids.push(id);
      }
      if (id && name) {
        hints[id] = name;
      }
    }
  }

  for (const id of asIdList(rec['tagIds'] ?? rec['TagIds'])) {
    if (!ids.includes(id)) {
      ids.push(id);
    }
  }

  const names = asNameList(rec['tagNames'] ?? rec['TagNames']);
  ids.forEach((id, index) => {
    if (!hints[id] && names[index]) {
      hints[id] = names[index];
    }
  });
  if (ids.length === 1 && names.length === 1 && !hints[ids[0]]) {
    hints[ids[0]] = names[0];
  }

  return { tagIds: ids, tagNameHints: hints };
}

export function liveUavSurveyTagFields(
  survey: unknown,
  tagsById: ReadonlyMap<string, string>,
): { tagIds: string[]; tagNames: string } {
  const { tagIds } = resolveUavSurveyTags(survey);
  if (tagIds.length > 0) {
    const liveIds: string[] = [];
    const names: string[] = [];
    const seen = new Set<string>();
    for (const id of tagIds) {
      if (seen.has(id)) {
        continue;
      }
      seen.add(id);
      const name = tagsById.get(id);
      if (name) {
        liveIds.push(id);
        names.push(name);
      }
    }
    return { tagIds: liveIds, tagNames: names.join(', ') };
  }

  const rec = asRecord(survey) ?? {};
  const known = new Set([...tagsById.values()]);
  const names: string[] = [];
  const seen = new Set<string>();
  for (const name of asNameList(rec['tagNames'] ?? rec['TagNames'])) {
    if (seen.has(name) || !known.has(name)) {
      continue;
    }
    seen.add(name);
    names.push(name);
  }
  return { tagIds: [], tagNames: names.join(', ') };
}

/** Drops deleted dictionary tags from UAV GetByQuery extra fields used by the gallery and edit modal. */
export function withLiveDictionaryTags<T>(survey: T, tagsById: ReadonlyMap<string, string>): T {
  const rec = asRecord(survey);
  if (!rec) {
    return survey;
  }
  const { tagIds, tagNames } = liveUavSurveyTagFields(survey, tagsById);
  const next: Record<string, unknown> = {
    ...rec,
    tagIds,
    tagNames: tagNames || undefined,
  };
  if (Array.isArray(rec['tags'])) {
    next['tags'] = tagIds.map((id) => ({ id, name: tagsById.get(id) }));
  }
  if (Array.isArray(rec['uavTags'])) {
    next['uavTags'] = tagIds.map((id) => ({ id, name: tagsById.get(id) }));
  }
  if ('TagIds' in rec) {
    next['TagIds'] = tagIds;
  }
  if ('TagNames' in rec) {
    next['TagNames'] = tagNames || undefined;
  }
  return next as T;
}
